import {
  buildDateUpdateTasks,
  getSharedDateValue,
  useUpdateTasks,
} from 'features/tasks';
import React, { useMemo } from 'react';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { getDateFormat } from 'shared/utils/get-date-format';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export interface DateSubMenuProps
  extends Omit<
    React.ComponentProps<typeof PopupMenu>,
    'children' | 'onOutside'
  > {
  tasks: Task[];
  onClose: () => void;
}

const datePresets = [
  DatePresets.Today,
  DatePresets.Tomorrow,
  DatePresets.NextWeek,
];

export const DateSubMenu: React.FC<DateSubMenuProps> = ({
  tasks,
  onClose,
  ...rest
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();
  const user = useUser();
  const dateFormat = getDateFormat(user);
  const weekStartsOn = getWeekStartsOn(user);

  const value = useMemo<Timestamp | undefined>(
    () => getSharedDateValue(tasks),
    [tasks],
  );

  const onChange = (date?: Timestamp) => {
    const tasksToUpdate = buildDateUpdateTasks(tasks, date, value, {
      weekStartsOn,
      today,
    });

    if (!tasksToUpdate.length) {
      onClose();
      return;
    }

    updateTasks(tasksToUpdate);
    onClose();
  };

  const shortcuts = useMemo(
    () => ({
      [DatePresets.Today]: getPlatformShortcut(['Shift', 'T']),
      [DatePresets.Tomorrow]: getPlatformShortcut(['Alt', 'Shift', 'T']),
      [DatePresets.NextWeek]: getPlatformShortcut(['Shift', 'W']),
    }),
    [],
  );

  return (
    <DatepickerWithOptions
      {...rest}
      value={value}
      onChange={onChange}
      dateFormat={dateFormat}
      weekStartsOn={weekStartsOn}
      datePresets={datePresets}
      icon={IconOptions.Calendar}
      onOutside={onClose}
      canRemoveValue
      forceRemoveValueOption
      shortcuts={shortcuts}
      clearShortcut={getPlatformShortcut(['Shift', 'U'])}
    />
  );
};
